import { IProjectCompanyOption } from '@company/projectCompany/projectCompany.models';
import { ContentLoader, IOption, Icon, Tooltip } from '@gcl/react-core';
import './AllUsersList.scss';
import { IProjectTeam } from '@projectTeam/projectTeam.models';
import { IProjectUserProfile } from '@userProfile/projectUserProfile.models';
import * as React from 'react';
import { useGetAllProjectCompaniesWithRolesAndUsers } from '@company/projectCompany/projectCompany.queries';
import { useProjectId } from '@config/config.hooks';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { getIsCompanyEditable } from './UserList.hooks';
import { UserTable } from './UserTable/UserTable';

interface IAllUserListProps {
  companyOptions: IOption<IProjectCompanyOption>[] | undefined;
  hasUserSettingRight: boolean;
  hasUserRightAdminRight: boolean;
  isReadOnly: boolean;
  setSelectedTeam: React.Dispatch<React.SetStateAction<IProjectTeam | undefined>>;
  openConfirmDeleteTeamPortal: (e: any) => void;
  openTeamModal?: (e: any) => void;
  openMemberModal: (e: any, userProfile?: IProjectUserProfile) => void;
  isDirtyMemberForm: boolean;
  setSelectedCompanyForForm: React.Dispatch<React.SetStateAction<IOption<IProjectCompanyOption> | undefined>>;
  onOpenCompanyModal: (e: any) => void;
  onOpenMemberModal: (e: any, projectUserProfile?: IProjectUserProfile) => void;
  setSelectedMember: (value: React.SetStateAction<IProjectUserProfile | undefined>) => void;
}

export const AllUsersList = ({
  companyOptions,
  hasUserSettingRight,
  hasUserRightAdminRight,
  isReadOnly,
  setSelectedTeam,
  openConfirmDeleteTeamPortal,
  openMemberModal,
  openTeamModal,
  isDirtyMemberForm,
  setSelectedCompanyForForm,
  onOpenCompanyModal,
  onOpenMemberModal,
  setSelectedMember,
}: IAllUserListProps) => {
  const projectId = useProjectId();
  const {
    data: dataAllProjectCompaniesWithRolesAndUsers,
    isLoading: isLoadingAllProjectCompaniesWithRolesAndUsers,
    error: errorAllProjectCompaniesWithRolesAndUsers,
  } = useGetAllProjectCompaniesWithRolesAndUsers({ projectId });

  const maxCols = Math.max(
    ...(dataAllProjectCompaniesWithRolesAndUsers?.items?.map((s) => s.roles?.length ?? 0) ?? []),
  );
  return (
    <>
      <ApiErrorDisplay
        errors={[{ error: errorAllProjectCompaniesWithRolesAndUsers, genericError: 'Get all companies failed' }]}
      />
      <ContentLoader
        active={isLoadingAllProjectCompaniesWithRolesAndUsers}
        linePadding={10}
        paragraphConfigs={[{ suffixBlankRow: false, numberOfRows: 5, lineHeight: 10 }]}
      >
        <>
          {dataAllProjectCompaniesWithRolesAndUsers?.items?.map((projectCompanyWithRolesAndUsers) => {
            const { roles, allUserTeam } = projectCompanyWithRolesAndUsers;
            const companyOption = companyOptions?.find((s) => s.data?.id === projectCompanyWithRolesAndUsers.id);
            const isCompanyEditable = getIsCompanyEditable(isReadOnly, companyOption) ?? false;
            const hasEditRight = hasUserRightAdminRight && isCompanyEditable;

            return (
              <div
                className="gcl-gems-all-users-list-company-container"
                key={`all-users-list-company-${companyOption?.key}`}
              >
                <div className="gcl-gems-all-users-list-company-header ">
                  <div className="gcl-gems-all-users-list-company-header-name">{companyOption?.text}</div>
                  {hasUserSettingRight && isCompanyEditable ? (
                    <>
                      <Tooltip
                        overlay={`Edit ${companyOption?.data?.name}`}
                        id="gcl-gems-user-table-edit-company-btn-tooltip"
                      >
                        <Icon
                          data-testid="update-Company"
                          iconName="Pencil"
                          className="gcl-gems-user-list-company-icon"
                          onClick={(e) => {
                            setSelectedCompanyForForm(companyOption);
                            onOpenCompanyModal(e);
                          }}
                        />
                      </Tooltip>
                      <Tooltip
                        overlay={`Add ${companyOption?.data?.name} Member`}
                        id="gcl-gems-user-table-add-company-member-btn-tooltip"
                      >
                        <Icon
                          data-testid="add-User"
                          iconName="PersonPlus"
                          className="gcl-gems-user-list-company-icon"
                          onClick={(e) => {
                            setSelectedCompanyForForm(companyOption);
                            setSelectedMember(undefined);
                            onOpenMemberModal(e);
                          }}
                        />
                      </Tooltip>
                    </>
                  ) : undefined}
                </div>

                <UserTable
                  colNum={maxCols}
                  hasEditRight={hasEditRight}
                  setSelectedCompanyForForm={
                    setSelectedCompanyForForm
                      ? () => {
                          setSelectedCompanyForForm(companyOption);
                        }
                      : undefined
                  }
                  roles={roles}
                  teams={allUserTeam ? [allUserTeam] : []}
                  teamDisplayMode="allUsers"
                  openTeamModal={openTeamModal}
                  openConfirmDeleteTeamPortal={openConfirmDeleteTeamPortal}
                  setSelectedTeam={setSelectedTeam}
                  openMemberModal={openMemberModal}
                />
              </div>
            );
          })}
        </>
      </ContentLoader>
    </>
  );
};
